import { CdkPortalOutlet, Portal, TemplatePortal } from '@angular/cdk/portal';
import {
  afterNextRender,
  Component,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { PageHeader, TranslatePipe } from '@cmi/addin-core';
import { map } from 'rxjs';
import * as wordControls from '../lib/word-content-controls';
import {
  listenForDocumentSelectionChanges,
  unlistenForDocumentSelectionChanges
} from '../lib/word-document';
import { ReportsFormat } from './home/reports/reports-format/reports-format';
import { PropertyFormat } from './property-format/property-format';
import { TableOptions } from './shared/table-options/table-options';

@Component({
  selector: 'cmi-root',
  imports: [
    RouterOutlet,
    RouterLink,
    PageHeader,
    TranslatePipe,
    CdkPortalOutlet,
    PropertyFormat,
    TableOptions,
    ReportsFormat
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
  host: { class: 'flex flex-col h-screen' }
})
export class App implements OnDestroy {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _viewContainerRef = inject(ViewContainerRef);

  protected readonly hideShell = toSignal(
    this._router.events.pipe(
      map(() => (this._route.firstChild?.snapshot.data['hideShell'] as boolean) ?? false)
    ),
    { initialValue: false }
  );

  public readonly footerPortal = signal<Portal<unknown> | null>(null);
  public readonly showTableOptions = signal<boolean>(false);
  public readonly tableOptionsContentControlId = signal<number | null>(null);

  public readonly propertyFormatPortal = signal<Portal<unknown> | null>(null);
  public readonly propertyFormatContentControlId = signal<number | null>(null);

  public readonly reportsFormatContentControlId = signal<number | null>(null);
  private _knownReportSelectorsByControlId = new Map<number, string>();

  protected readonly propertyFormatPortalTemplate =
    viewChild<TemplateRef<unknown>>('propertyFormatTemplate');

  public constructor() {
    afterNextRender(() => {
      if (Office.context.document) {
        void this._setupSelectionChangeListener();
      }
    });
  }

  public ngOnDestroy(): void {
    if (Office.context.document) {
      void unlistenForDocumentSelectionChanges();
    }
  }

  private async _setupSelectionChangeListener(): Promise<void> {
    await listenForDocumentSelectionChanges(() => {
      void this._handleSelectionChange();
    });
    await this._syncDeletedReportSelectorProperties();
  }

  private async _handleSelectionChange(): Promise<void> {
    await this._syncDeletedReportSelectorProperties();

    await Word.run(async (context) => {
      const selectedContentControl = await this._findSelectedContentControl(context);

      if (
        !selectedContentControl.isNullObject &&
        selectedContentControl.title &&
        selectedContentControl.title.startsWith('CMR_')
      ) {
        this._hidePropertyFormatPortal();
        this._hideTableOptionsPortal();
        this._showReportsFormatPortal(selectedContentControl.id);
      } else if (
        !selectedContentControl.isNullObject &&
        selectedContentControl.title &&
        (selectedContentControl.title.startsWith('CMI_') ||
          selectedContentControl.title === 'ITEM' ||
          selectedContentControl.title.startsWith('ITEM:') ||
          selectedContentControl.title.startsWith('ITEM.'))
      ) {
        const contentControlRange = selectedContentControl.getRange();
        const tables = contentControlRange.tables;
        const parentTableCell = selectedContentControl.parentTableCellOrNullObject;
        tables.load('items');
        // Load a real scalar property to include the proxy in the sync batch.
        // 'isNullObject' cannot be passed to load() — it is set automatically after sync.
        parentTableCell.load('rowIndex');
        await context.sync();

        const isInsideTable = !parentTableCell.isNullObject;
        const hasTable = !isInsideTable && tables.items.length > 0;

        this._hideReportsFormatPortal();
        if (hasTable) {
          // Show table options inline via portal
          this._hidePropertyFormatPortal();
          this._showTableOptionsPortal(selectedContentControl.id);
        } else {
          // Show property format inline via portal
          this._hideTableOptionsPortal();
          this._showPropertyFormatPortal(selectedContentControl.id);
        }
      } else {
        // Selection is not in a content control or not a CMI/CMR control
        this._handleContentControlDeselection();
      }
    });
  }

  private async _syncDeletedReportSelectorProperties(): Promise<void> {
    try {
      const allControls = await wordControls.getAllContentControls();
      const currentReportSelectorsByControlId = new Map<number, string>();

      for (const control of allControls) {
        if (!control.title?.startsWith('CMR_')) {
          continue;
        }

        const selector = wordControls.parseContentControlTag(control.tag).selector;
        if (selector) {
          currentReportSelectorsByControlId.set(control.id, selector);
        }
      }

      const removedSelectors = new Set<string>();
      for (const [controlId, selector] of this._knownReportSelectorsByControlId) {
        if (!currentReportSelectorsByControlId.has(controlId)) {
          removedSelectors.add(selector);
        }
      }

      for (const selector of removedSelectors) {
        await wordControls.removeReportSelectorPropertiesIfUnused(selector);
      }

      this._knownReportSelectorsByControlId = currentReportSelectorsByControlId;
    } catch {
      // Ignore cleanup failures to avoid breaking editor interactions.
    }
  }

  private async _findSelectedContentControl(
    context: Word.RequestContext
  ): Promise<Word.ContentControl> {
    const selection = context.document.getSelection();

    selection.load('parentContentControlOrNullObject');
    await context.sync();

    const directParent = selection.parentContentControlOrNullObject;
    if (directParent.isNullObject) {
      return directParent;
    }

    directParent.load(['id', 'title', 'parentContentControlOrNullObject']);
    await context.sync();

    if (this._isManagedContentControlTitle(directParent.title)) {
      return directParent;
    }

    // In nested scenarios (e.g. controls inside table wrappers), walk up to find
    // the closest control we can handle in the UI.
    let currentParent = directParent;
    for (let depth = 0; depth < 10; depth++) {
      const nextParent = currentParent.parentContentControlOrNullObject;
      if (nextParent.isNullObject) {
        break;
      }

      nextParent.load(['id', 'title', 'parentContentControlOrNullObject']);
      await context.sync();

      if (this._isManagedContentControlTitle(nextParent.title)) {
        return nextParent;
      }

      currentParent = nextParent;
    }

    return directParent;
  }

  private _isManagedContentControlTitle(title: string | null | undefined): boolean {
    if (!title) {
      return false;
    }

    return (
      title.startsWith('CMR_') ||
      title.startsWith('CMI_') ||
      title === 'ITEM' ||
      title.startsWith('ITEM:') ||
      title.startsWith('ITEM.')
    );
  }

  private _handleContentControlDeselection(): void {
    const hasTableOptionsPortal = this.tableOptionsContentControlId() !== null;
    const hasPropertyFormatPortal = this.propertyFormatContentControlId() !== null;
    const hasReportsFormatPortal = this.reportsFormatContentControlId() !== null;

    if (hasTableOptionsPortal) {
      this._hideTableOptionsPortal();
    }

    if (hasPropertyFormatPortal) {
      this._hidePropertyFormatPortal();
    }

    if (hasReportsFormatPortal) {
      this._hideReportsFormatPortal();
    }
  }

  private _showTableOptionsPortal(contentControlId: number): void {
    this.tableOptionsContentControlId.set(contentControlId);
    this.showTableOptions.set(true);
  }

  private _hideTableOptionsPortal(): void {
    if (this.tableOptionsContentControlId() !== null) {
      this.tableOptionsContentControlId.set(null);
      this.showTableOptions.set(false);
    }
  }

  private _showPropertyFormatPortal(contentControlId: number): void {
    this.propertyFormatContentControlId.set(contentControlId);
    const template = this.propertyFormatPortalTemplate();
    if (template) {
      const portal = new TemplatePortal(template, this._viewContainerRef);
      this.propertyFormatPortal.set(portal);
    }
  }

  private _hidePropertyFormatPortal(): void {
    if (this.propertyFormatContentControlId() !== null) {
      this.propertyFormatContentControlId.set(null);
      this.propertyFormatPortal.set(null);
    }
  }

  private _showReportsFormatPortal(contentControlId: number): void {
    this.reportsFormatContentControlId.set(contentControlId);
  }

  private _hideReportsFormatPortal(): void {
    if (this.reportsFormatContentControlId() !== null) {
      this.reportsFormatContentControlId.set(null);
    }
  }

  public readonly notificationPortal = signal<Portal<unknown> | null>(null);
}
